/**
 * src/core/VoiceRegistry.js
 *
 * Manages a catalogue of named voice classes and the single currently-active voice.
 *
 * Voice contract — every registered class must implement:
 *   static defaultParams()                → object   (required — enables introspection)
 *   new VoiceClass(ctx, destination, cfg) → instance
 *   instance.start(liveParam)
 *   instance.update(liveParam)
 *   instance.stop(onDone?)
 *   instance.dispose()
 *
 * Usage:
 *   const registry = new VoiceRegistry();
 *   registry.register('waterFlow', WaterFlowVoice)
 *           .register('heartbeat', HeartbeatVoice)
 *           .register('recipe',    RecipeVoice);
 *
 *   registry.start('waterFlow', ctx, destination, params, liveParam);
 *   registry.update(0.4);
 *   registry.stopActive();
 *
 *   // Introspect:
 *   registry.defaultParams('waterFlow'); // → WaterFlowVoice.defaultParams()
 *   registry.voiceNames;                 // → ['waterFlow', 'heartbeat', 'recipe']
 */
export class VoiceRegistry {
    constructor() {
        this._classes = new Map(); // name → VoiceClass
        this._active = null; // { name, voice } | null
    }

    /**
     * Register a voice class under a name. Chainable.
     *
     * @param {string}   name
     * @param {Function} VoiceClass  - must have static defaultParams()
     * @returns {this}
     */
    register(name, VoiceClass) {
        if (typeof VoiceClass !== "function") {
            throw new TypeError(
                `Voice "${name}": expected a class/constructor, got ${typeof VoiceClass}`,
            );
        }
        if (typeof VoiceClass.defaultParams !== "function") {
            throw new TypeError(
                `Voice "${name}" must implement static defaultParams() so the registry ` +
                    `can introspect its parameter schema.`,
            );
        }
        this._classes.set(name, VoiceClass);
        return this;
    }

    /**
     * Unregister a voice (e.g. for hot-reloading in the demo).
     * If it is currently active, it is stopped first.
     *
     * @param {string} name
     */
    unregister(name) {
        if (this._active?.name === name) this.stopActive();
        this._classes.delete(name);
    }

    /**
     * Start a named voice. Stops whatever is currently playing first.
     *
     * @param {string}      name
     * @param {AudioContext} ctx
     * @param {AudioNode}   destination
     * @param {object}      params     - merged cfg passed to the constructor
     * @param {number}      liveParam  - initial live parameter value
     * @returns {object}  the voice instance
     */
    start(name, ctx, destination, params, liveParam = 0.5) {
        this.stopActive();
        const VoiceClass = this._classes.get(name);
        if (!VoiceClass)
            throw new Error(
                `VoiceRegistry: unknown voice "${name}". Registered: [${[...this._classes.keys()].join(", ")}]`,
            );
        const voice = new VoiceClass(ctx, destination, params);
        voice.start(liveParam);
        this._active = { name, voice };
        return voice;
    }

    /**
     * Update the live parameter of the currently active voice.
     * No-op if nothing is playing.
     *
     * @param {number} liveParam
     */
    update(liveParam) {
        this._active?.voice.update(liveParam);
    }

    /**
     * Stop and tear down the active voice.
     *
     * @param {Function} [onDone]  - called after the release tail finishes
     */
    stopActive(onDone) {
        if (!this._active) {
            onDone?.();
            return;
        }
        const { voice } = this._active;
        this._active = null;
        voice.stop(onDone);
    }

    // ── Introspection ──────────────────────────────────────────────────────────

    /** Name of the currently active voice, or null. */
    get activeName() {
        return this._active?.name ?? null;
    }

    /** Sorted list of all registered voice names. */
    get voiceNames() {
        return [...this._classes.keys()];
    }

    /**
     * Return the default parameter object for a registered voice.
     * Useful for populating a UI without instantiating the voice.
     *
     * @param {string} name
     * @returns {object}
     */
    defaultParams(name) {
        const VC = this._classes.get(name);
        if (!VC) throw new Error(`VoiceRegistry: unknown voice "${name}"`);
        return VC.defaultParams();
    }
}
